import React, { useState } from 'react';
import { Tr, Td } from '../../ui/Table';
import Checkbox from '../../ui/Checkbox';
import { FileText, Download, Copy, Trash2, Pencil, Plus } from 'lucide-react';
import Input from '../../ui/Input';
import Select from '../../ui/Select';
import Button from '../../ui/Button';

const DetailItem = ({ label, value }) => (
  <div className="flex flex-col gap-1 mb-4">
    <span className="font-semibold text-gray-900 text-[12px] uppercase tracking-wider">{label}</span>
    <span className="text-gray-600 text-[13px]">{value || '-'}</span>
  </div>
);

const HistoryTableRow = ({
  inv,
  isChecked,
  handleCheckboxChange,
  handleGeneratePdf,
  handleDownloadPdf,
  handleLoadInvoiceToForm,
  handleDeleteInvoice,
  handleUpdateInvoice,
  bankDetails = []
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editableInvoice, setEditableInvoice] = useState(null);

  const toggleExpand = () => {
    if (isEditing) return; // Do not toggle if editing
    setIsExpanded(!isExpanded);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setEditableInvoice(JSON.parse(JSON.stringify(inv)));
    setIsEditing(true);
    setIsExpanded(true);
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditableInvoice(null);
  };

  const handleSaveInvoice = (e) => {
    e.stopPropagation();
    if (handleUpdateInvoice && editableInvoice) {
      // Recalculate total amount before save
      const totalAmount = editableInvoice.items.reduce((sum, item) => sum + (item.itemQuantity * item.itemPrice), 0);
      handleUpdateInvoice({ ...editableInvoice, totalAmount: totalAmount.toFixed(2) });
    }
    setIsEditing(false);
    setEditableInvoice(null);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setEditableInvoice(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'paymentMethod' && value === 'CASH') {
        updated.issuerBankDetailsId = null;
        updated.issuerBankDetails = null;
      }
      return updated;
    });
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    setEditableInvoice(prev => {
      const newItems = [...prev.items];
      newItems[index] = { 
        ...newItems[index], 
        [name]: name === 'itemQuantity' || name === 'itemPrice' ? Number(value) : value 
      };
      return { ...prev, items: newItems };
    });
  };

  const handleAddItem = () => {
    setEditableInvoice(prev => ({
      ...prev,
      items: [
        ...(prev.items || []),
        { itemName: '', itemQuantity: 1, itemPrice: 0 }
      ]
    }));
  };

  const handleRemoveItem = (index) => {
    setEditableInvoice(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const getPaymentMethod = (method) => {
    if (method === 'CASH') return 'В брой';
    if (method === 'BANK_TRANSFER') return 'По банков път';
    return method;
  };

  return (
    <>
      <Tr 
        className={`group cursor-pointer hover:bg-gray-50/50 transition-colors ${isExpanded ? 'bg-gray-50/50' : 'border-b border-gray-50 last:border-0'}`}
        onClick={toggleExpand}
      >
        <Td className="text-center" onClick={(e) => e.stopPropagation()}>
          <Checkbox 
            checked={isChecked}
            onChange={(e) => handleCheckboxChange(inv.id, e.target.checked)}
          />
        </Td>
        <Td className="font-semibold text-gray-900">{inv.invoiceNumber}</Td>
        <Td className="max-w-[200px] truncate" title={inv.receiverName}>{inv.receiverName}</Td>
        <Td>{inv.creationDate}</Td>
        <Td>{inv.totalAmount}</Td>
        <Td onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
            <button onClick={(e) => handleGeneratePdf && handleGeneratePdf(e, inv)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-tenant-hover rounded-md transition-colors bg-white" title="Преглед">
              <FileText size={16} strokeWidth={2} />
            </button>
            <button onClick={(e) => handleDownloadPdf && handleDownloadPdf(e, inv)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-tenant-hover rounded-md transition-colors bg-white" title="Изтегли">
              <Download size={16} strokeWidth={2} />
            </button>
            <button onClick={() => handleLoadInvoiceToForm && handleLoadInvoiceToForm(inv)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-tenant-hover rounded-md transition-colors bg-white" title="Дублирай/Зареди">
              <Copy size={16} strokeWidth={2} />
            </button>
            <button onClick={handleEditClick} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-blue-600 rounded-md transition-colors bg-white" title="Редактирай">
              <Pencil size={16} strokeWidth={2} />
            </button>
            <button onClick={() => handleDeleteInvoice && handleDeleteInvoice(inv)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 rounded-md transition-colors bg-white" title="Изтрий">
              <Trash2 size={16} strokeWidth={2} />
            </button>
          </div>
        </Td>
      </Tr>

      {isExpanded && !isEditing && (
        <Tr className="border-b border-gray-100 bg-gray-50/30">
          <Td colSpan={6} className="p-0">
            <div className="p-6 overflow-hidden">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
                <DetailItem label="Номер на фактура" value={inv.invoiceNumber} />
                <DetailItem label="Получател" value={inv.receiverName} />
                <DetailItem label="ДДС Номер" value={inv.receiverVatNumber} />
                <DetailItem label="Идентификационен номер" value={inv.receiverIdNumber} />
                
                <DetailItem label="Адрес" value={inv.receiverAddress} />
                <DetailItem label="МОЛ" value={inv.receiverMol} />
                <DetailItem label="Телефон" value={inv.receiverPhoneNumber} />
                <DetailItem label="Обща сума" value={`€ ${inv.totalAmount}`} />
                
                <DetailItem label="Обща сума с думи" value={inv.totalAmountInWords} />
                <DetailItem label="Метод на плащане" value={getPaymentMethod(inv.paymentMethod)} />
                {inv.paymentMethod === 'BANK_TRANSFER' && (
                  <DetailItem label="Банкова сметка" value={inv.iban ? `${inv.bank} - ${inv.iban}` : ''} />
                )}
                <DetailItem label="Описание на сделката" value={inv.dealDescription} />
                
                <DetailItem label="Дата на издаване" value={inv.creationDate ? new Date(inv.creationDate).toLocaleDateString() : ''} />
                <DetailItem label="Дата на падеж" value={inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : ''} />
                <DetailItem label="Дата на данъчно събитие" value={inv.taxEventDate ? new Date(inv.taxEventDate).toLocaleDateString() : ''} />
              </div>

              {inv.items && inv.items.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200/60">
                  <span className="font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-3 block">Артикули:</span>
                  <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                    <table className="w-full text-left text-[13px]">
                      <thead className="bg-gray-50/80">
                        <tr>
                          <th className="px-4 py-2 font-medium text-gray-500">Име</th>
                          <th className="px-4 py-2 font-medium text-gray-500 text-right w-24">Количество</th>
                          <th className="px-4 py-2 font-medium text-gray-500 text-right w-32">Цена (бр.)</th>
                          <th className="px-4 py-2 font-medium text-gray-500 text-right w-32">Общо</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {inv.items.map(item => (
                          <tr key={item.id} className="hover:bg-gray-50/50">
                            <td className="px-4 py-2 text-gray-900">{item.itemName}</td>
                            <td className="px-4 py-2 text-gray-600 text-right">{item.itemQuantity}</td>
                            <td className="px-4 py-2 text-gray-600 text-right">€ {item.itemPrice}</td>
                            <td className="px-4 py-2 text-gray-900 text-right font-medium">€ {(item.itemQuantity * item.itemPrice).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </Td>
        </Tr>
      )}

      {isExpanded && isEditing && editableInvoice && (
        <Tr className="border-b border-gray-100 bg-gray-50/30">
          <Td colSpan={6} className="p-0">
            <div className="p-6 overflow-hidden border-2 border-blue-100 rounded-xl m-2 bg-white">
              <h3 className="font-bold text-gray-900 mb-4 text-[15px]">Редакция на фактура</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
                <div>
                  <span className="block font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-1">Номер на фактура</span>
                  <Input name="invoiceNumber" value={editableInvoice.invoiceNumber || ''} onChange={handleFieldChange} />
                </div>
                <div>
                  <span className="block font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-1">Получател</span>
                  <Input name="receiverName" value={editableInvoice.receiverName || ''} onChange={handleFieldChange} />
                </div>
                <div>
                  <span className="block font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-1">ДДС Номер</span>
                  <Input name="receiverVatNumber" value={editableInvoice.receiverVatNumber || ''} onChange={handleFieldChange} />
                </div>
                <div>
                  <span className="block font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-1">Идентификационен номер</span>
                  <Input name="receiverIdNumber" value={editableInvoice.receiverIdNumber || ''} onChange={handleFieldChange} />
                </div>
                <div>
                  <span className="block font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-1">Адрес</span>
                  <Input name="receiverAddress" value={editableInvoice.receiverAddress || ''} onChange={handleFieldChange} />
                </div>
                <div>
                  <span className="block font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-1">МОЛ</span>
                  <Input name="receiverMol" value={editableInvoice.receiverMol || ''} onChange={handleFieldChange} />
                </div>
                <div>
                  <span className="block font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-1">Телефон</span>
                  <Input name="receiverPhoneNumber" value={editableInvoice.receiverPhoneNumber || ''} onChange={handleFieldChange} />
                </div>
                <div>
                  <span className="block font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-1">Дата на издаване</span>
                  <Input type="date" name="creationDate" value={editableInvoice.creationDate || ''} onChange={handleFieldChange} />
                </div>
                <div>
                  <span className="block font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-1">Дата на падеж</span>
                  <Input type="date" name="dueDate" value={editableInvoice.dueDate || ''} onChange={handleFieldChange} />
                </div>
                <div>
                  <span className="block font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-1">Метод на плащане</span>
                  <Select name="paymentMethod" value={editableInvoice.paymentMethod || 'CASH'} onChange={handleFieldChange}>
                    <option value="CASH">В брой</option>
                    <option value="BANK_TRANSFER">По банков път</option>
                  </Select>
                </div>
                {editableInvoice.paymentMethod === 'BANK_TRANSFER' && (
                  <div>
                    <span className="block font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-1">Банкова сметка</span>
                    <Select
                      name="issuerBankDetailsId"
                      value={editableInvoice.issuerBankDetailsId || (editableInvoice.issuerBankDetails?.id) || ''}
                      onChange={handleFieldChange}
                    >
                      <option value="" disabled>Изберете банкова сметка</option>
                      {bankDetails && bankDetails.map(bd => (
                        <option key={bd.id} value={bd.id}>{bd.iban}</option>
                      ))}
                    </Select>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <span className="block font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-1">Описание на сделката</span>
                  <Input name="dealDescription" value={editableInvoice.dealDescription || ''} onChange={handleFieldChange} />
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-gray-900 text-[12px] uppercase tracking-wider block">Артикули:</span>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <div className="grid grid-cols-[1fr_100px_100px_40px] gap-2 mb-2 px-1 hidden sm:grid">
                    <div className="text-[12px] font-semibold text-gray-500 uppercase tracking-wider">Име</div>
                    <div className="text-[12px] font-semibold text-gray-500 uppercase tracking-wider">К-во</div>
                    <div className="text-[12px] font-semibold text-gray-500 uppercase tracking-wider">Цена</div>
                  </div>
                  {editableInvoice.items && editableInvoice.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-1 sm:grid-cols-[1fr_100px_100px_40px] gap-2 mb-2 items-center group">
                      <Input name="itemName" value={item.itemName || ''} onChange={(e) => handleItemChange(index, e)} placeholder="Име" />
                      <Input type="number" name="itemQuantity" value={item.itemQuantity || ''} onChange={(e) => handleItemChange(index, e)} placeholder="Кол." />
                      <Input type="number" name="itemPrice" value={item.itemPrice || ''} onChange={(e) => handleItemChange(index, e)} placeholder="Цена" />
                      <div className="flex justify-center mt-2 sm:mt-0">
                        {editableInvoice.items.length > 1 && (
                          <button type="button" onClick={() => handleRemoveItem(index)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100" title="Изтрий ред">
                            <Trash2 size={16} strokeWidth={2} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mt-4 gap-4">
                    <Button
                      type="button"
                      onClick={handleAddItem}
                      variant="secondary"
                      size="sm"
                      className="gap-2 text-tenant-dark border-tenant-dark/20 hover:bg-tenant-light w-full sm:w-auto"
                    >
                      <Plus size={16} strokeWidth={2} />
                      Добави ред
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <Button variant="secondary" onClick={handleCancelEdit}>Отказ</Button>
                <Button variant="primary" onClick={handleSaveInvoice}>Запази</Button>
              </div>
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default HistoryTableRow;
